import { useState, useContext } from "react";
import { SidebarContext } from "../../../shared/context/SidebarContext";
import { fetchLeads } from "../services/trackingServiceV2";
import { toast } from "react-toastify";
import DateFilterBar from "../components/DateFilterBar";
import JornadaModal, { UtmBadges } from "../components/JornadaModal";

// Leads = quem deixou e-mail (evento `lead`) nas páginas do produto no período.
// Checkout vira coluna (clicou / chegou), não linha. Compra = venda do produto no período.

function formatR$(v) {
    return "R$ " + Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
}

export default function CheckoutLeads() {
    const { isOpen } = useContext(SidebarContext);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [jornadaVisitor, setJornadaVisitor] = useState(null);

    async function handleBuscar(startDate, endDate, filters = {}) {
        setLoading(true);
        try {
            setData(await fetchLeads(startDate, endDate, filters));
        } catch {
            toast.error("Erro ao buscar leads");
        }
        setLoading(false);
    }

    return (
        <div className="min-h-screen bg-surface-1 text-white flex">
            <main className={`transition-all duration-300 ${isOpen ? "lg:ml-64" : "sm:ml-16"} w-full p-6 mb-16 sm:mb-0`}>
                <header className="mb-6">
                    <h1 className="text-3xl font-bold mb-1">Checkout Leads</h1>
                    <p className="text-sm text-ink-3 mb-4">Quem deixou e-mail nas páginas do produto, se foi ao checkout e se comprou.</p>
                    <DateFilterBar onBuscar={handleBuscar} loading={loading} />
                </header>

                {!data && !loading && <p className="text-center text-ink-4 py-8">Selecione um período e clique em Buscar</p>}
                {loading && <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" /></div>}

                {data && !loading && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                            {[
                                { label: "Leads", value: data.kpis.total, color: "text-blue-400" },
                                { label: "Clicaram no checkout", value: data.kpis.clicaram_checkout, color: "text-yellow-400" },
                                { label: "Chegaram ao checkout", value: data.kpis.chegaram_checkout, color: "text-orange-400" },
                                { label: "Compraram", value: data.kpis.compraram, color: "text-emerald-400" },
                                { label: "Não Compraram", value: data.kpis.nao_compraram, color: "text-red-400" },
                            ].map(kpi => (
                                <div key={kpi.label} className="bg-surface-2 p-4 rounded-lg">
                                    <p className="text-xs text-ink-3">{kpi.label}</p>
                                    <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
                                </div>
                            ))}
                        </div>

                        <div className="bg-surface-2 rounded-lg overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-white/[0.08] text-ink-3">
                                        <th className="text-left p-3">Nome</th>
                                        <th className="text-left p-3">Email</th>
                                        <th className="text-left p-3">WhatsApp</th>
                                        <th className="text-left p-3">Checkout</th>
                                        <th className="text-left p-3">Status</th>
                                        <th className="text-left p-3">Valor / Produto</th>
                                        <th className="text-left p-3">Lead em</th>
                                        <th className="text-left p-3">UTMs</th>
                                        <th className="text-left p-3">Ação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.leads.map((lead) => (
                                        <tr key={lead.visitor_id} className="border-b border-white/[0.08] hover:bg-surface-3/30">
                                            <td className="p-3">{lead.nome || "—"}</td>
                                            <td className="p-3 text-ink-2">{lead.email || "—"}</td>
                                            <td className="p-3">
                                                {lead.telefone ? (
                                                    <a href={`https://wa.me/${lead.telefone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline">{lead.telefone}</a>
                                                ) : "—"}
                                            </td>
                                            <td className="p-3 text-xs">
                                                {lead.chegou_checkout ? <span className="bg-orange-900/40 text-orange-300 px-2 py-0.5 rounded-full">Chegou</span>
                                                    : lead.clicou_checkout ? <span className="bg-yellow-900/40 text-yellow-300 px-2 py-0.5 rounded-full">Clicou</span>
                                                        : <span className="text-ink-5">—</span>}
                                            </td>
                                            <td className="p-3">
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${lead.status === "comprou" ? "bg-emerald-900/50 text-emerald-400" : "bg-blue-900/50 text-blue-400"}`}>
                                                    {lead.status === "comprou" ? "Comprou" : "Lead"}
                                                </span>
                                            </td>
                                            <td className="p-3">
                                                {lead.status === "comprou" && lead.valor_compra ? (
                                                    <div>
                                                        <p className="text-emerald-400 font-medium text-xs">{formatR$(lead.valor_compra)}</p>
                                                        {lead.nome_produto && <p className="text-ink-4 text-[10px] truncate max-w-[150px]">{lead.nome_produto}</p>}
                                                    </div>
                                                ) : <span className="text-ink-5 text-xs">{lead.produto_nome || "—"}</span>}
                                            </td>
                                            <td className="p-3 text-ink-3 text-xs whitespace-nowrap">{lead.created_at ? new Date(lead.created_at).toLocaleString("pt-BR") : "—"}</td>
                                            <td className="p-3"><UtmBadges utms={{ utm_source: lead.utm_source, utm_medium: lead.utm_medium, utm_campaign: lead.utm_campaign, utm_term: lead.utm_term, utm_content: lead.utm_content }} /></td>
                                            <td className="p-3"><button onClick={() => setJornadaVisitor(lead.visitor_id)} className="text-emerald-400 text-xs hover:underline">Ver Jornada</button></td>
                                        </tr>
                                    ))}
                                    {data.leads.length === 0 && <tr><td colSpan={9} className="text-center text-ink-4 p-6">Nenhum lead encontrado</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </main>

            {jornadaVisitor && <JornadaModal visitorId={jornadaVisitor} onClose={() => setJornadaVisitor(null)} />}
        </div>
    );
}
